import React, { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { useFormikContext } from 'formik';

import TraroElement from 'types/api/TraroElement';
import TraroElementResponse from 'types/api/TraroElementResponse';
import QuestionStatus from 'types/QuestionStatus';

import FormikTextInput from 'components/forms/formik/FormikTextInput/FormikTextInput';
import { AutoSaveIndicator } from 'components/AutoSaveWatcher';
import Note from 'components/Note/Note';
import Button from 'components/Button/Button';
import useUser from 'hooks/useUser';

import KliparoQuestion from './components/KliparoQuestion/KliparoQuestion';
import { FormValues } from '../../hooks/useInitialValues';

import './QuestionResponse.scss';

export interface QuestionResponseProps {
  traroElement: TraroElement;
  rfpElementResponse: TraroElementResponse;
}

const QuestionResponse: FC<QuestionResponseProps> = ({ traroElement, rfpElementResponse }) => {
  const { t } = useTranslation();
  const { isReviewer } = useUser();
  const { submitForm, values, isValid, setFieldValue } = useFormikContext<FormValues>();

  const handleSubmit = useCallback(() => {
    if (!isReviewer && isValid) {
      if (values.questionStatus === QuestionStatus.DONE) {
        setFieldValue('questionStatus', QuestionStatus.IN_PROGRESS);
      } else if (
        values.questionStatus === QuestionStatus.IN_PROGRESS ||
        values.questionStatus === QuestionStatus.BEGIN
      ) {
        setFieldValue('questionStatus', QuestionStatus.DONE);
      }
    }

    submitForm();
  }, [submitForm, values, isValid, setFieldValue]);

  const sortedQuestions = useMemo(
    () => [...traroElement.kliparo_question_list].sort((a, b) => a.question_number - b.question_number),
    [traroElement.kliparo_question_list],
  );

  return (
    <div className="enkrateia-kliparo-question-response">
      <Note variant="kliparo">
        <div>
          <ReactMarkdown skipHtml linkTarget="_blank" className="question-1">
            {traroElement.question_text1}
          </ReactMarkdown>
          {traroElement.question_text2 && (
            <ReactMarkdown linkTarget="_blank" className="question-2">
              {traroElement.question_text2}
            </ReactMarkdown>
          )}
        </div>
      </Note>

      <AutoSaveIndicator />

      <div className="form-data">
        {sortedQuestions.map((question) => (
          <KliparoQuestion
            key={question.id}
            disabled={isReviewer || rfpElementResponse.question_status === QuestionStatus.DONE}
            question={question}
          />
        ))}
      </div>

      {!isReviewer && (
        <Button
          variant={rfpElementResponse.question_status === QuestionStatus.DONE ? 'primary' : 'tertiary-filled'}
          onClick={handleSubmit}
          disabled={rfpElementResponse.question_status === QuestionStatus.REVIEW_ONLY}
        >
          {rfpElementResponse.question_status === QuestionStatus.DONE
            ? t('pages.traroElement.markAsInProgress')
            : t('pages.traroElement.markAsComplete')}
        </Button>
      )}

      {isReviewer && (
        <div className="score">
          <FormikTextInput
            label={t('pages.traroElement.score')}
            name="score"
            type="number"
            min={0}
            max={traroElement.max_score}
          />
          <Button variant="primary" type="button" onClick={handleSubmit}>
            {t('pages.traroElement.saveScore')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default QuestionResponse;
